/**
 * ProviderDocsPanel — Integration Hub
 * -----------------------------------------------------------------------------
 * Renderiza a documentação de um conector (markdown vindo do banco) de forma
 * segura: `marked` converte o markdown e `sanitizeHtml` remove qualquer vetor
 * de XSS antes da exibição.
 *
 * O conteúdo NÃO é hardcoded em componente: vem de
 * `integration_providers.overview_md / setup_guide_md` e de
 * `integration_operations.help_md`, de modo que novos providers só precisam de
 * linhas no banco — nenhuma tela muda.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (documentação por conector).
 */

import { useMemo } from 'react';
import { marked } from 'marked';
import { sanitizeHtml } from '@/lib/sanitize';
import { cn } from '@/lib/utils';

interface MarkdownBlockProps {
  /** Conteúdo markdown (pode ser nulo) */
  content?: string | null;
  className?: string;
}

/** Bloco de markdown sanitizado e estilizado com a tipografia do sistema */
export function MarkdownBlock({ content, className }: MarkdownBlockProps) {
  const html = useMemo(() => {
    if (!content) return '';
    // `marked.parse` síncrono (sem async extensions) + sanitização obrigatória
    return sanitizeHtml(marked.parse(content, { async: false }) as string);
  }, [content]);

  if (!html) return null;

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-sm text-muted-foreground',
        '[&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
        '[&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground',
        '[&_a]:text-primary [&_a]:underline',
        '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
        '[&_p]:my-2 [&_strong]:text-foreground',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-3 [&_blockquote]:italic',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
        className,
      )}
      // Conteúdo já sanitizado acima
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

interface ProviderDocsPanelProps {
  providerName: string;
  overviewMd?: string | null;
  setupGuideMd?: string | null;
  docsUrl?: string | null;
  supportUrl?: string | null;
}

/** Aba "Documentação" do conector */
export function ProviderDocsPanel({
  providerName,
  overviewMd,
  setupGuideMd,
  docsUrl,
  supportUrl,
}: ProviderDocsPanelProps) {
  const hasContent = Boolean(overviewMd || setupGuideMd);

  return (
    <div className="space-y-6">
      {!hasContent && (
        <p className="text-sm text-muted-foreground">
          Ainda não há documentação cadastrada para o {providerName}. Consulte os links abaixo ou
          fale com o suporte do Uaizy.
        </p>
      )}

      {overviewMd && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Sobre o aplicativo</h3>
          <MarkdownBlock content={overviewMd} />
        </section>
      )}

      {setupGuideMd && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Passo a passo da configuração</h3>
          <MarkdownBlock content={setupGuideMd} />
        </section>
      )}

      {(docsUrl || supportUrl) && (
        <section className="flex flex-wrap gap-4 border-t pt-4 text-xs">
          {docsUrl && (
            <a href={docsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              Documentação oficial
            </a>
          )}
          {supportUrl && (
            <a
              href={supportUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Suporte do aplicativo
            </a>
          )}
        </section>
      )}
    </div>
  );
}
